/**
 * useFirstTraceNotification — listens to the SSE milestone channel and
 * surfaces a "first real trace received — audit chain extended" toast
 * + invalidates the onboarding state cache so the checklist instantly
 * crosses off the trace_received step.
 *
 * SYNTHESIS_2026-05-05 §3.6 — the aha-moment notification. Kept inside
 * the dashboard layout so a trace landing while the user has any page
 * open still triggers the celebration.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useTraceStream, type MilestoneEvent } from './useTraceStream';
import { queryKeys } from './useAPI';
import { trackEvent } from '../services/analytics';

interface Options {
  enabled?: boolean;
}

export function useFirstTraceNotification({ enabled = true }: Options = {}) {
  const queryClient = useQueryClient();

  const onMilestone = useCallback((event: MilestoneEvent) => {
    if (event.milestone !== 'first_trace_received') return;
    toast.success('First real trace received', {
      description: 'Your audit chain just extended. The decision is now visible in the Decision Log.',
      duration: 8000,
    });
    trackEvent('onboarding_first_trace_milestone' as any, {
      traceId: event.traceId,
    });
    // Refresh derived checklist + recent activity so trace_received flips
    // to ✓ on the same tick.
    queryClient.invalidateQueries({ queryKey: queryKeys.onboardingState });
    queryClient.invalidateQueries({ queryKey: queryKeys.traces() });
  }, [queryClient]);

  // Subscribe (no buffering — milestones are per-event, not displayed list)
  useTraceStream({ enabled, onMilestone, bufferSize: 0 });
}
